import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Skeleton
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import { contactsAPI } from '../../../services/api';
import { contactDetailStyles, animations } from './styles';

const ContactAISummary = ({ contact, onContactUpdate }) => {
    const [status, setStatus] = useState(contact?.aiSummaryStatus || 'none');
    const [summary, setSummary] = useState(contact?.aiSummary || null);
    const pollRef = useRef(null);

    useEffect(() => {
        setStatus(contact?.aiSummaryStatus || 'none');
        setSummary(contact?.aiSummary || null);
    }, [contact?.aiSummaryStatus, contact?.aiSummary]);

    useEffect(() => {
        if (status === 'generating') {
            pollRef.current = setInterval(async () => {
                try {
                    const response = await contactsAPI.getContact(contact._id);
                    const updated = response.data;
                    if (updated.aiSummaryStatus !== 'generating') {
                        setStatus(updated.aiSummaryStatus);
                        setSummary(updated.aiSummary);
                        clearInterval(pollRef.current);
                        if (onContactUpdate) {
                            onContactUpdate(updated);
                        }
                    }
                } catch (error) {
                    console.error('Error polling summary status:', error);
                }
            }, 3000);
        }

        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
            }
        };
    }, [status, contact?._id, onContactUpdate]);

    const handleRegenerate = async () => {
        try {
            setStatus('generating');
            await contactsAPI.regenerateSummary(contact._id);
        } catch (error) {
            console.error('Error regenerating summary:', error);
            setStatus('error');
        }
    };

    if (!contact?.interactions || contact.interactions.length === 0) {
        return null;
    }

    return (
        <Box sx={{ ...contactDetailStyles.sectionContainer, ...animations.fadeInUp }}>
            <Box sx={contactDetailStyles.sectionHeader}>
                <Typography variant="h6" sx={contactDetailStyles.sectionTitle}>
                    <AutoAwesomeIcon fontSize="small" />
                    AI Summary
                </Typography>
                <IconButton
                    size="small"
                    onClick={handleRegenerate}
                    disabled={status === 'generating'}
                    title="Regenerate summary"
                >
                    <RefreshIcon fontSize="small" />
                </IconButton>
            </Box>

            {status === 'generating' && (
                <Box>
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="90%" />
                    <Skeleton variant="text" width="70%" />
                </Box>
            )}

            {status === 'ready' && summary && (
                <Typography
                    variant="body2"
                    sx={contactDetailStyles.summaryText}
                >
                    {summary}
                </Typography>
            )}

            {(status === 'none' || status === 'error' || (!summary && status !== 'generating')) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                    <Typography
                        variant="body2"
                        sx={contactDetailStyles.emptyText}
                    >
                        {status === 'error'
                            ? 'Failed to generate summary.'
                            : 'No summary generated yet.'}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={handleRegenerate}
                    >
                        Generate Summary
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default ContactAISummary;
